import audiencesBrutes from '../donnees/audiences.json' with { type: 'json' };
import politiquesBrutes from '../donnees/politique-conflits.json' with { type: 'json' };
import type { SujetSync } from '../types.js';

/**
 * Politiques de synchronisation lues dans des DONNÉES versionnées (`src/donnees/*.json`), jamais écrites en dur :
 * conflit par type d'entité, types lisibles et écrivables par audience. Statut « a_valider » : question BETA à Isabelle.
 * Un type ou une audience absents des données n'ont aucun droit (refus par défaut).
 */
export type ModeConflit = 'ajout' | 'champs';

export interface PolitiqueConflit {
  readonly mode: ModeConflit;
  /** Champs dont le serveur est prioritaire : une écriture sur base périmée est rejetée, jamais présentée comme conflit. */
  readonly champsServeur: readonly string[];
}

export interface DroitsAudience {
  readonly lecture: readonly string[];
  readonly ecriture: readonly string[];
}

const MODES: readonly string[] = ['ajout', 'champs'];

function verifierPolitiques(brut: unknown): Readonly<Record<string, PolitiqueConflit>> {
  const entites = (brut as { entites?: Record<string, { mode?: unknown; champsServeur?: unknown }> }).entites ?? {};
  const sortie: Record<string, PolitiqueConflit> = Object.create(null) as Record<string, PolitiqueConflit>;
  for (const [type, p] of Object.entries(entites)) {
    if (typeof p.mode !== 'string' || !MODES.includes(p.mode))
      throw new Error(`Politique de conflit invalide pour ${type} : mode ${String(p.mode)}.`);
    if (!Array.isArray(p.champsServeur) || p.champsServeur.some((c) => typeof c !== 'string')) {
      throw new Error(`Politique de conflit invalide pour ${type} : champsServeur.`);
    }
    sortie[type] = { mode: p.mode as ModeConflit, champsServeur: p.champsServeur as string[] };
  }
  return sortie;
}

function verifierAudiences(brut: unknown): Readonly<Record<string, DroitsAudience>> {
  const audiences = (brut as { audiences?: Record<string, { lecture?: unknown; ecriture?: unknown }> }).audiences ?? {};
  const sortie: Record<string, DroitsAudience> = Object.create(null) as Record<string, DroitsAudience>;
  for (const [nom, a] of Object.entries(audiences)) {
    const listes = [a.lecture, a.ecriture];
    if (listes.some((l) => !Array.isArray(l) || l.some((t) => typeof t !== 'string'))) throw new Error(`Audience invalide : ${nom}.`);
    sortie[nom] = { lecture: a.lecture as string[], ecriture: a.ecriture as string[] };
  }
  return sortie;
}

const POLITIQUES = verifierPolitiques(politiquesBrutes);
const AUDIENCES = verifierAudiences(audiencesBrutes);
const AUCUN_DROIT: DroitsAudience = { lecture: [], ecriture: [] };

export const VERSION_POLITIQUES: string = politiquesBrutes.version;

export function politiqueDe(entityType: string): PolitiqueConflit | null {
  return POLITIQUES[entityType] ?? null;
}

export function audienceDe(audience: string): DroitsAudience {
  return AUDIENCES[audience] ?? AUCUN_DROIT;
}

/**
 * Portée d'un sujet sur un dossier, commune au pull et au push. Refus par défaut (arbitrage 4 du 16/10) :
 * une portée sans liste (`null`) ne vaut « tous les dossiers » que pour l'équipe ; un client sans liste n'a accès à rien.
 * Une entité sans dossier n'est accessible qu'à l'équipe.
 */
export function dansLaPortee(sujet: SujetSync, dossierId: string | null): boolean {
  if (sujet.dossiersAutorises === null || dossierId === null) return sujet.audience === 'equipe';
  return sujet.dossiersAutorises.includes(dossierId);
}
